import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "../../config/config";

const BlogCard = ({ url, title, description, author, date, tags, blogData }) => {
    const navigate = useNavigate();

    const handleClick = (e) => {
        e.preventDefault();
        navigate(url, { state: { blogData } });
    };
    return (
        <a
            href={url}
            onClick={handleClick}
            className="group relative flex flex-col h-full bg-white border-[1.5px] border-gray-300 rounded-[28px] shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_40px_-15px_rgba(28,72,107,0.2)] hover:border-[#1C486B]/30 transition-all duration-500 overflow-hidden hover:-translate-y-2 block"
        >
            {/* Decorative Blob/Gradient in background */}
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-48 h-48 bg-gradient-to-br from-[#1C486B]/10 to-[#EB7E0E]/10 rounded-full blur-3xl group-hover:from-[#1C486B]/20 group-hover:to-[#EB7E0E]/20 transition-colors duration-500 pointer-events-none"></div>

            <div className="flex flex-col h-full p-8 relative z-10">
                {/* Top Header: Tags + Arrow Icon */}
                <div className="flex justify-between items-start mb-6 gap-4">
                    <div className="flex flex-wrap gap-2">
                        {tags && tags.slice(0, 2).map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-[#1C486B]/5 text-[#1C486B] border border-[#1C486B]/10 tracking-wide uppercase">
                                {tag}
                            </span>
                        ))}
                        {tags && tags.length > 2 && (
                            <span className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-bold bg-gray-50 text-gray-500 border border-gray-100 tracking-wide">
                                +{tags.length - 2}
                            </span>
                        )}
                    </div>

                    <div className="w-11 h-11 rounded-full bg-gray-50 group-hover:bg-gradient-to-tr group-hover:from-[#1C486B] group-hover:to-[#2A6B9E] shadow-sm group-hover:shadow-[0_4px_12px_rgba(28,72,107,0.3)] flex items-center justify-center transition-all duration-300 flex-shrink-0 border border-gray-100 group-hover:border-transparent">
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-white transform group-hover:-rotate-45 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </div>
                </div>

                {/* Title & Description */}
                <h3 className="text-2xl font-bold text-[#1C486B] mb-4 leading-[1.3] group-hover:text-[#EB7E0E] transition-colors duration-300 pr-2">
                    {title}
                </h3>

                <p className="text-gray-600 text-[15px] leading-relaxed mb-8 flex-grow line-clamp-3">
                    {description}
                </p>

                {/* Footer: Metadata */}
                <div className="mt-auto pt-5 border-t border-gray-100 flex flex-col gap-3">
                    {author && (
                        <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                            <div className="w-8 h-8 rounded-full bg-blue-50/80 flex items-center justify-center">
                                <svg className="w-4 h-4 text-[#1C486B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <span className="truncate">{author}</span>
                        </div>
                    )}
                    {date && (
                        <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                            <div className="w-8 h-8 rounded-full bg-orange-50/80 flex items-center justify-center">
                                <svg className="w-4 h-4 text-[#EB7E0E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <span>{date}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Accent Line */}
            <div className="h-1.5 w-full bg-gradient-to-r from-[#1C486B] via-[#2A6B9E] to-[#EB7E0E] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left absolute bottom-0 left-0"></div>
        </a>
    );
};

// Function to format date from ISO string to "Month Day, Year" format
const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

// Function to transform API data to component format
const transformBlogData = (apiBlog) => {
    // Remove trailing hyphens from slug for cleaner URLs
    const cleanSlug = apiBlog.slug ? apiBlog.slug.replace(/-+$/, '') : "";

    return {
        url: `/blogs/${cleanSlug}`,
        title: apiBlog.title || "",
        description: apiBlog.subheading || "",
        author: apiBlog.authorname || "",
        date: formatDate(apiBlog.updatedAt),
        tags: apiBlog.tags || []
    };
};

function Text() {
    const [blogs, setBlogs] = useState([]);
    const [allBlogsData, setAllBlogsData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await fetch(`${SERVER_URL}/api/blog/blogForWebside`);
                const result = await response.json();

                if (result.data && Array.isArray(result.data)) {
                    // Store full blog data
                    setAllBlogsData(result.data);
                    // Transform for display
                    const transformedBlogs = result.data.map(transformBlogData);
                    setBlogs(transformedBlogs);
                }
            } catch (error) {
                console.error("Error fetching blogs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);

    return (
        <div className="w-full px-6 md:px-12 lg:px-16 xl:px-20 py-8 max-md:max-w-full">
            <div className="mb-8">
                <h2 className="mb-2 text-3xl md:text-4xl font-bold text-[#1C486B]">Latest Blogs</h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#1C486B] to-[#EB7E0E] rounded-full"></div>
            </div>
            {loading ? (
                <div className="text-center py-8 text-gray-600">Loading blogs...</div>
            ) : (
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {blogs.map((blog, index) => (
                        <BlogCard
                            key={index}
                            {...blog}
                            blogData={allBlogsData[index]}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default Text;
